import { useEffect, useMemo, useState, useContext } from "react";

import { Drawer } from "@mui/material";
import ReserveTableWidget from "../../components/ReserveTableWidget";
import {
    useAssignCustomerToTableData,
    useReserveTableData,
    useTableOrders,
} from "../../shared/hooks/useTableOrdersData";
import OrderWidget from "../../components/OrderWidget";
import { useTableOrderItems } from "../../shared/hooks/useTableOrderItemsData";
import moment from "moment";
import WidgetsDialogs from "./WidgetsDialogs";
import { useOrderInvoiceData } from "../../shared/hooks/useInvoicesData";
import { AppContext } from "../../AppContext";
import { enqueueSnackbar } from "notistack";
import { PosContext } from "../../shared/PosContext";

export default function ReservationDialog({ onClose }) {
    const { globalState, setGlobalState } = useContext(AppContext);
    const { refetchTablesData } = useContext(PosContext);

    const [reserveDialogOpen, setReserveDialogOpen] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);

    const reserveTableMutation = useReserveTableData(); //room, tableFormData
    const assignTableMutation = useAssignCustomerToTableData(); //room, tableFormData
    const { isLoading } = reserveTableMutation;
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const { data: tableOrders } = useTableOrders(selectedTable?.name || null);

    const { data: orderItems } = useTableOrderItems(
        selectedOrder?.name || null
    );
    const [items, setItems] = useState([]);
    const [customerScreenVisible, setCustomerScreenVisible] = useState(false);

    const { data: invoiceData, refetch } = useOrderInvoiceData(
        selectedOrder ? selectedOrder.name : null
    );
    const [widgetDialogTable, setWidgetDialogTable] = useState(null);
    const [widgetsDialogsOpen, setWidgetsDialogsOpen] = useState(false);

    useMemo(() => {
        if (orderItems) {
            setItems(Object.values(orderItems.items));
        } else {
            setItems([]);
        }
    }, [orderItems]);

    useEffect(() => {
        document.addEventListener("reserveTable", handleReserveTable);
        return () => {
            document.removeEventListener("reserveTable", handleReserveTable);
        };
    }, []);

    // Define the event handler function
    function handleReserveTable(event) {
        // Retrieve the table details from the event
        const table = event.detail.table;

        if (table.reserved) {
            setCustomerScreenVisible(false);
            setReserveDialogOpen(true);
        } else {
            setCustomerScreenVisible(true);
            setReserveDialogOpen(true);
        }

        // Perform any actions with the table details
        setSelectedTable(table);
    }

    const onConfirm = (customerName) => {
        if (!customerName || !selectedTable) {
            enqueueSnackbar("Select Cusotmer");
            return;
        }

        setReserveDialogOpen(false);

        if (selectedTable.reserved && selectedOrder) {
            const date = new Date();

            const formattedDate = moment(date).format(
                "YYYY-MM-DD HH:mm:ss.SSSSSSSSS"
            );
            let payload = {
                name: selectedOrder.name,
                owner: globalState.loggedInUser,
                creation: formattedDate,
                modified: formattedDate,
                table: selectedOrder.table,
                doctype: "Table Order",
                customer: customerName,
            };

            assignTableMutation.mutate({
                orderId: selectedOrder.name,
                payload: payload,
            });
        } else {
            reserveTableMutation.mutate(
                {
                    tableId: selectedTable.name,
                    customerName: customerName,
                },
                {
                    onSuccess: (data, { tableId }) => {
                        refetchTablesData()
                    },
                }
            );
        }
    };

    const handleSelectOrder = (order) => {
        if (order) {
            setSelectedOrder(order);
        }
    };

    const handleChangeCustomer = (customer) => {
        setSelectedCustomer(customer);
        setTimeout(() => {
            setCustomerScreenVisible(true);
        }, 1000); // Delay of 2000 milliseconds (2 seconds)
    };

    const handleCloseAll = () => {
        setSelectedOrder(null);
        setSelectedTable(null);
        setSelectedCustomer(null);
        setReserveDialogOpen(false);
    };

    const handleCloseTable = (order, table) => {
        setWidgetDialogTable(table);
        refetch();
        setWidgetsDialogsOpen(true);
    };

    const handleCloseDialog = () => {
        setWidgetsDialogsOpen(false);
    };

    function renderSearchCustomerWidget() {
        if (customerScreenVisible) {
            return (
                <Drawer anchor="right" open={true} onClose={handleCloseAll}>
                    <ReserveTableWidget
                        preSelectedCustomer={selectedCustomer}
                        isLoading={isLoading}
                        onConfirm={onConfirm}
                        selectedTable={selectedTable}
                        backCallback={handleCloseAll}
                    ></ReserveTableWidget>
                </Drawer>
            );
        } else {
            return (
                <Drawer
                    anchor="right"
                    open={true}
                    onClose={handleCloseAll}
                    PaperProps={{
                        style: {
                            backgroundColor: "#f5f6fa",
                        },
                    }}
                >
                    <div style={{ margin: "20px", background: "#f5f6fa" }}>
                        <OrderWidget
                            selectedOrder={selectedOrder}
                            table={selectedTable}
                            items={items}
                            handleSelectOrder={handleSelectOrder}
                            handleChangeCustomer={handleChangeCustomer}
                            handleCloseTable={handleCloseTable}
                            tableOrders={tableOrders}
                            isLoading={isLoading}
                            selectedTable={selectedTable}
                            minified={true}
                        ></OrderWidget>
                    </div>
                </Drawer>
            );
        }
    }

    return (
        <>
            {reserveDialogOpen && renderSearchCustomerWidget()}

            <WidgetsDialogs
                open={widgetsDialogsOpen}
                onClose={handleCloseDialog}
                table={widgetDialogTable}
                order={selectedOrder}
                invoiceData={invoiceData}
            ></WidgetsDialogs>
        </>
    );
}
